import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import InHouseBarChart from "../../../components/UIToolkit/Graphs/BarChart/InHouseBarChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { MultiFacilityDisciplinarySummaryResponse } from "../../../utils/models";
import { MULTI_CHART_HEIGHTS } from "./MultiFacilityChartCard";
import { MultiFacilityChartStateBox } from "./MultiFacilityChartStateBox";
import { DISCIPLINARY_VIOLATION_ORDER, dedupeFacilityDisplayNames, disciplinaryToStackedBarGroups, hasAnyDisciplinaryData } from "./utils";

/** Longest facility name shown on an X tick before truncation (the tooltip keeps the full name). */
const X_TICK_MAX_CHARS = 14;
/** X-axis strip height fitting X_TICK_MAX_CHARS-char labels angled at -45° without clipping. */
const X_AXIS_HEIGHT = 68;
/** One stacked bar per facility (few, categorical) — wide bars so per-violation segments read clearly. */
const BAR_VISIBLE_WIDTH = 32;

type Props = {
	/** Disciplinary summary for the whole portfolio; undefined while loading. */
	data?: MultiFacilityDisciplinarySummaryResponse;
	isLoading: boolean;
	isError: boolean;
	/** Sorted, stable facility order — the tie-break order for equal violation totals. */
	sortedDepartmentIds: string[];
	/**
	 * Page-selected subset of `sortedDepartmentIds` (order-preserving): only these facilities get a
	 * bar. Display-name dedupe stays over the FULL set so a "(2)"-suffixed facility keeps its
	 * suffix even when its duplicate is deselected.
	 */
	visibleDepartmentIds: string[];
};

/**
 * Full-width disciplinary comparison card: one stacked bar per facility (worst-first) with the five
 * violation types as color-coded segments. Legend toggles are handled inside the bar chart; this
 * card only reports them to analytics.
 */
export const MultiFacilityDisciplinaryCard: FC<Props> = ({ data, isLoading, isError, sortedDepartmentIds, visibleDepartmentIds }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();

	const violationLabels = useMemo(
		() => Object.fromEntries(DISCIPLINARY_VIOLATION_ORDER.map((violation) => [violation, t(`disciplinary.violation.${violation}`)])),
		[t],
	);

	// Bars are built from the page-selected subset only (the helper re-ranks worst-first among the
	// subset); display names dedupe over the FULL set so suffixes stay stable across selections.
	const stackedGroups = useMemo(() => {
		if (isLoading || !data) return undefined;
		const displayNames = dedupeFacilityDisplayNames(sortedDepartmentIds, data.facilityNames);
		return disciplinaryToStackedBarGroups({ perFacility: data.perFacility, sortedDepartmentIds: visibleDepartmentIds, displayNames, violationLabels });
	}, [isLoading, data, sortedDepartmentIds, visibleDepartmentIds, violationLabels]);

	const onSeriesVisibilityToggle = useCallback(
		(series: string, visible: boolean) => {
			track("management_center_multi_series_toggled", { series, visible });
		},
		[track],
	);

	// Selection-aware empty check: all-zero counts across the SELECTED facilities read as "no data"
	// even when a deselected facility has violations.
	const visiblePerFacility = useMemo(
		() => (data ? Object.fromEntries(visibleDepartmentIds.filter((id) => data.perFacility[id]).map((id) => [id, data.perFacility[id]])) : undefined),
		[data, visibleDepartmentIds],
	);
	const isEmpty = !isLoading && !isError && visiblePerFacility !== undefined && !hasAnyDisciplinaryData(visiblePerFacility);

	return (
		<CommonDashboardBox
			data-testid="mc-multi-disciplinary-chart"
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "start",
				gap: 1,
				width: "100%",
			}}
		>
			<CommonDashboardHeader label={t("disciplinary.summary")} />
			{isError || isEmpty ? (
				<MultiFacilityChartStateBox isError={isError} height={MULTI_CHART_HEIGHTS} />
			) : (
				<InHouseBarChart
					width={"100%"}
					height={MULTI_CHART_HEIGHTS}
					stackedGroups={stackedGroups}
					xAxisType="category"
					xTickFormatter={(label) => {
						const text = typeof label === "string" ? label : label.toFormat("MMM d");
						return text.length > X_TICK_MAX_CHARS ? `${text.slice(0, X_TICK_MAX_CHARS - 1)}…` : text;
					}}
					xAxisHeight={X_AXIS_HEIGHT}
					barVisibleWidth={BAR_VISIBLE_WIDTH}
					onSeriesVisibilityToggle={onSeriesVisibilityToggle}
					shrinkLegendForSmallScreens
				/>
			)}
		</CommonDashboardBox>
	);
};
